// packages import
import { useApolloClient } from '@apollo/client'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useDebounce } from 'use-debounce'
// component import
import { useCallback, useContext, useEffect, useState } from 'react'
import { ActiveCasesListing } from './ActiveCasesListing'
import { SearchCases } from './SearchCases'
// others
import { CaseStatus, OrderCaseBy, UserType, useFetchAllCasesLightLazyQuery } from '../../../../generated'
import { ActiveCaseListingItem } from '../../../interfaceTypes'
import { AuthContext } from '../../../context'
import { CardBox } from '../../../theme/styleComponents'

export const AllActiveCases = () => {
  const [activeCases, setActiveCases] = useState<ActiveCaseListingItem[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearchTerm] = useDebounce(searchValue, 500)
  const { currentUser } = useContext(AuthContext)
  const { id: userId } = currentUser || {}
  const apolloClient = useApolloClient()

  const [fetchAllCasesLight, { loading }] = useFetchAllCasesLightLazyQuery({
    fetchPolicy: 'cache-first',
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { fetchAllCasesLight: result } = data
        const { cases, response } = result || {}
        const { status } = response || {}

        if (status === 200 && cases) {
          const filteredCases = cases?.filter(caseItem =>
            caseItem?.caseToUsers?.some(
              user => ![UserType.Creator].includes(user?.userType as UserType) && user?.userId === userId
            )
          )

          setActiveCases(prevCases => (prevCases ? [...prevCases, ...filteredCases] : filteredCases))
        }
      }
    }
  })

  const handleRefresh = useCallback(() => {
    apolloClient.cache.evict({ fieldName: 'fetchAllCasesLight' })
    apolloClient.cache.gc()
    setCurrentPage(1)
    setActiveCases([])
    fetchAllCasesLight({
      variables: {
        casesLightInput: {
          userId,
          status: CaseStatus.Active,
          isWorkCompleted: false,
          searchValue: debouncedSearchTerm,
          orderBy: OrderCaseBy.MatterNo,
          paginationOptions: { limit: 10, page: 1 }
        }
      },
      fetchPolicy: 'network-only'
    })
  }, [apolloClient, fetchAllCasesLight, userId, debouncedSearchTerm])

  useEffect(() => {
    fetchAllCasesLight({
      variables: {
        casesLightInput: {
          userId,
          status: CaseStatus.Active,
          isWorkCompleted: false,
          searchValue: debouncedSearchTerm,
          orderBy: OrderCaseBy.MatterNo,

          paginationOptions: {
            limit: 10,
            page: currentPage
          }
        }
      }
    })
  }, [fetchAllCasesLight, userId, currentPage, debouncedSearchTerm])

  useEffect(() => {
    setCurrentPage(1)
    setActiveCases([])
  }, [debouncedSearchTerm])

  return (
    <Box width="100%" position="relative">
      <Typography fontSize="24px" fontWeight={400}>
        All Active Cases
      </Typography>

      <CardBox p="24px" mt="16px" minHeight="450px">
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Typography fontSize="20px" fontWeight={500}>
            Cases
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Refresh to load latest">
              <IconButton onClick={handleRefresh} disabled={loading} size="small" aria-label="Refresh cases">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <SearchCases searchValue={searchValue} setSearchValue={setSearchValue} />
          </Box>
        </Box>

        <ActiveCasesListing filterActiveACases={activeCases || []} loading={loading} setCurrentPage={setCurrentPage} />
      </CardBox>
    </Box>
  )
}
